import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import LocationLinks from './LocationLinks';

export interface SEOPageProps {
  city: string;
  state: string;
  route: string;
  imageSrc: string;
  services: string[];
  airports: string[];
  neighborhoods?: string[];
  introduction: string;
  testimonial: {
    quote: string;
    author: string;
    role: string;
  };
  fboLink?: {
    url: string;
    name: string;
  };
}

const SEOPageTemplate = ({
  city,
  state,
  route,
  imageSrc,
  services,
  airports,
  neighborhoods = [],
  introduction,
  testimonial,
  fboLink,
}: SEOPageProps) => {
  const pageTitle = `Luxury Chauffeur Service in ${city}, ${state} | TiLimousine`;
  const metaDescription = `Premium chauffeur service in ${city}, ${state}. Book luxury black car, FBO pickup, and executive transportation. Professional drivers, 24/7 service, and competitive rates.`;
  const canonicalUrl = `https://tilimousine.com${route}`;
  const ogImage = `https://tilimousine.com${imageSrc}`;
  
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'Service'],
    '@id': `https://tilimousine.com${route}#business`,
    name: `TiLimousine - Luxury Chauffeur Service in ${city}`,
    url: canonicalUrl,
    areaServed: [
      {
        '@type': 'City',
        name: city,
        containedIn: {
          '@type': 'State',
          name: state
        }
      },
      {
        '@type': 'State',
        name: state
      }
    ],
    serviceType: ['Luxury Chauffeur Service', 'Black Car Service', 'Airport Transportation', 'FBO Pickup Service'],
    description: `Premium black car, livery, FBO pickup, and airport chauffeur service in ${city}, ${state}. Professional drivers, luxury vehicles, and 24/7 availability.`,
    telephone: '+1-857-312-3332',
    email: 'info@tilimousine.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: state,
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '42.3601',  // Example coordinates for Boston
      longitude: '-71.0589'
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ],
      opens: '00:00',
      closes: '23:59'
    },
    priceRange: '$$$',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '500+',
      bestRating: '5',
      worstRating: '1'
    },
    review: {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5'
      },
      author: {
        '@type': 'Person',
        name: testimonial.author
      },
      reviewBody: testimonial.quote,
      datePublished: new Date().toISOString().split('T')[0]
    },
    makesOffer: services.map(service => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service,
        provider: {
          '@type': 'LocalBusiness',
          name: 'TiLimousine',
          '@id': `https://tilimousine.com${route}#business`
        }
      },
      priceSpecification: {
        '@type': 'PriceSpecification',
        priceCurrency: 'USD',
        minPrice: '75',
        maxPrice: '500'
      }
    })),
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'TiLimousine Services',
      itemListElement: services.map((service, index) => ({
        '@type': 'OfferCatalog',
        position: index + 1,
        itemOffered: {
          '@type': 'Service',
          name: service
        }
      }))
    },
    faqPage: {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `What luxury chauffeur services do you offer in ${city}?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `We provide comprehensive luxury transportation services in ${city}, including airport transfers, FBO pickups, corporate travel, hourly as-directed service, and special event transportation.`
          }
        },
        {
          '@type': 'Question',
          name: 'Can your chauffeur wait at my private terminal?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, we offer wait-and-greet service at all major FBOs including Signature Flight Support, Atlantic Aviation, and Sheltair. Our chauffeurs are familiar with private terminal procedures.'
          }
        },
        {
          '@type': 'Question',
          name: 'Do you offer hourly bookings?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Yes, we provide flexible hourly service throughout ${city} with professional chauffeurs. Perfect for meetings, events, or as-directed transportation needs.`
          }
        },
        {
          '@type': 'Question',
          name: "What's the difference between your service and Uber Black?",
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our service offers professional chauffeurs with extensive local knowledge, privacy-focused transportation, and dedicated customer support. We specialize in FBO pickups, corporate travel, and luxury transportation with a personal touch.'
          }
        }
      ]
    }
  };

  const trackEvent = (event: { action: string; category: string; label: string }) => {
    // Implement your analytics tracking here
    console.log('Track event:', event);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={`${city} chauffeur, ${city} car service, ${state} chauffeur, luxury car service ${city}, airport transfer ${city}, corporate transportation ${city}, FBO pickup ${city}, private jet transportation ${city}`} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="TiLimousine" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:site" content="@TiLimousine" />
        
        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="author" content="TiLimousine" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://tilimousine.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": "https://tilimousine.com/services"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": city,
                "item": `https://tilimousine.com${route}`
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": `Chauffeur Service in ${city}`,
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "reviewCount": "27"
            },
            "review": [
              {
                "@type": "Review",
                "author": testimonial?.author,
                "reviewBody": testimonial?.quote,
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5"
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <Navbar />
      
      <div className="container mx-auto px-4 py-16">
        {/* Breadcrumb Navigation */}
        <nav className="mb-8 flex items-center space-x-2 text-sm">
          <Link to="/" className="text-gold hover:text-gold/80">Home</Link>
          <span className="text-gold">/</span>
          <Link to="/services" className="text-gold hover:text-gold/80">Services</Link>
          <span className="text-gold">/</span>
          <span className="text-white">Chauffeur Service in {city}</span>
        </nav>

        <h1 className="text-4xl md:text-5xl font-cinzel text-gold mb-8">Luxury Chauffeur Service in {city}, {state}</h1>
        
        {/* Hero Image */}
        <div className="relative w-full h-[400px] mb-12 rounded-lg overflow-hidden">
          <img 
            src={imageSrc} 
            alt={`Luxury chauffeur service in ${city} with black car at FBO`} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Introduction */}
        <div className="prose prose-invert max-w-none mb-12">
          <p className="text-xl leading-relaxed">{introduction}</p>
        </div>

        {/* Why Choose TiLimousine Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Why Choose TiLimousine in {city}?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Background-Checked Chauffeurs</h3>
              <p>Our professional drivers undergo rigorous background checks and extensive training to ensure your safety and comfort.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Local FBO Experience</h3>
              <p>Expert knowledge of all FBOs and private terminals in {city}, ensuring seamless private jet transportation.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Hourly Flexibility</h3>
              <p>Book by the hour for maximum flexibility, perfect for meetings, events, or as-directed service throughout {city}.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Business-Class Vehicles</h3>
              <p>Modern, well-maintained luxury vehicles equipped with amenities for a premium travel experience.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gold mb-4">Responsive Dispatch</h3>
              <p>24/7 dispatch team ready to assist with booking changes, special requests, or last-minute arrangements.</p>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="mb-12">
          <blockquote className="border-l-4 border-gold pl-6 py-4">
            <p className="text-xl italic mb-4">"{testimonial.quote}"</p>
            <footer className="text-gold">
              <cite>– {testimonial.author}, {testimonial.role}</cite>
            </footer>
          </blockquote>
        </section>

        {/* Services Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Our Services in {city}</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-gold mr-2">•</span>
                <span>{service}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Airports Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Airports & FBOs We Serve</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {airports.map((airport, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-gold mr-2">•</span>
                <span>{airport}</span>
              </li>
            ))}
          </ul>
          {fboLink && (
            <div className="mt-4">
              <a 
                href={fboLink.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold hover:text-gold/80 underline"
              >
                {fboLink.name}
              </a>
            </div>
          )}
        </section>

        {/* Neighborhoods Section */}
        {neighborhoods.length > 0 && (
          <section className="mb-12">
            <h2 className="text-3xl font-cinzel text-gold mb-8">Neighborhoods & Local Areas</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {neighborhoods.map((n, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="text-gold mr-2">•</span>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* FAQ Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Frequently Asked Questions</h2>
          <ul className="space-y-6">
            <li>
              <strong className="block text-xl mb-2">Can your chauffeur wait at my private terminal?</strong>
              <p>Yes, we offer wait-and-greet at all major FBOs including Signature Flight Support, Atlantic Aviation, and Sheltair.</p>
            </li>
            <li>
              <strong className="block text-xl mb-2">Do you offer hourly bookings?</strong>
              <p>Absolutely. We provide flexible as-directed pricing with professional chauffeurs available for hourly service throughout ' + city + '.</p>
            </li>
            <li>
              <strong className="block text-xl mb-2">What's the difference between your service and Uber Black?</strong>
              <p>Our service offers professional chauffeurs with extensive local knowledge, privacy-focused transportation, and dedicated customer support. We specialize in FBO pickups and corporate travel.</p>
            </li>
          </ul>
        </section>

        {/* Related Cities */}
        <section className="mb-12">
          <h2 className="text-3xl font-cinzel text-gold mb-8">Explore Our Services in Nearby Cities</h2>
          <LocationLinks currentCity={city} currentState={state} />
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-3xl font-cinzel text-gold mb-6">Ready to Experience Premium Transportation?</h2>
          <p className="text-xl mb-8">Book your luxury chauffeur service in {city} today</p>
          <button
            onClick={() => trackEvent({
              action: 'click',
              category: 'CTA',
              label: `Book Now - ${city}`,
            })}
            className="inline-block bg-gold text-black px-8 py-3 rounded-full hover:bg-gold/80 transition-colors font-semibold"
          >
            Book Now
          </button>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default SEOPageTemplate; 